import { z } from 'zod';
import { percentageSchema, priceSchema } from '../validation';
import { currencySchema } from './currency.schema';

//* tax id
export const taxIdSchema = z.enum(['saudi_vat']);
export type TaxId = z.infer<typeof taxIdSchema>;

//* tax
export const taxSchema = z.object({
  /** the name of the tax */
  id: taxIdSchema,
  /** tax percentage such as 0.15 which means 15% tax on an amount **/
  percentage: percentageSchema,
  /**
   * the actual tax amount such as (100 SAR subtotal x 0.15 VAT = 15 SAR tax amount)
   *
   * so the tax amount is 15 SAR
   *  */
  amount: priceSchema,
  /** the custom subtotal that we're supposed to use to calculate the tax.
   *
   * This is used if you have sum of multiple amounts and one has tax and the other has no tax.
   *
   * so you can provide the subtotal that you want to calculate the tax on it.
   *
   * This **subtotal** must be after all fees and discounts.
   *
   * If it's empty, then we will use the parent `subtotal` field to calculate the tax in the `Amount` object.
   */
  taxOnCustomSubtotal: priceSchema.optional(),
});
/** tax info (name, percentage, amount) */
export type Tax = z.infer<typeof taxSchema>;
/** tax name & percentage */
export const taxInfoSchema = taxSchema.pick({ id: true, percentage: true });
/** tax name & percentage */
export type TaxInfo = Readonly<z.infer<typeof taxInfoSchema>>;

export type TaxesList = Readonly<{
  [key in TaxId]: TaxInfo;
}>;
//* multi tax
export const multiTaxSchema = z.object({
  /** the total tax amount. It's going to be the sum of taxes applied array */
  total: priceSchema,
  /** a list of the taxes applied on total */
  applied: z.array(taxSchema).default([]),
});
export type MultiTax = z.infer<typeof multiTaxSchema>;

//* fees
export const feeTypeSchema = z.enum([
  'overnight_stay',
  'destination_waiting_too_long',
  'destination_late_arrival',
  'items_insurance',
  'additional_location', // when the user add new drop off / or pickup location.
  'other',
]);
export type FeeType = z.infer<typeof feeTypeSchema>;
export const feeCalcTypeSchema = z.enum(['subtract_from_subtotal', 'add_to_subtotal', 'realized']);
export type FeeCalcType = z.infer<typeof feeCalcTypeSchema>;
export const feeSchema = z.object({
  /** the type of fees
   * - `overnight_stay` = when the truck stays overnight on the loading/unloading fees
   * - `destination_late_arrival` = when there is a delay in arriving at a destination (**mostly carrier problem**)
   * - `destination_waiting_too_long` = when there is a too much time waiting at a destination (**mostly customer problem**)
   * - `items_insurance` = extra fees to apply for goods/items insurance.
   * - `other` = any other fee that can be added to the total
   */
  type: feeTypeSchema,
  /** the amount of the fee */
  amount: priceSchema,
  /**
   * the calculation type that we're supposed to use to add the fee.
   *
   * - `subtract_from_subtotal` = means subtract the fee amount from the subtotal amount
   * - `add_to_subtotal` = means add the fee amount on top of the subtotal amount
   * - `realized` = means we do not add or subtract any value, and it does not effect the subtotal ... it effects the realized amount.
   * this could be used to acknowledge the fee only with is amount NOT to pay it
   */
  calcType: feeCalcTypeSchema,
});
export type Fee = z.infer<typeof feeSchema>;

export const amountFeesSchema = z.object({
  /** the total to be paid which is the difference between
   *
   * added (`add_to_subtotal`) `-` subtrac.,.,,joited (`subtract_from_subtotal`)
   * */
  total: priceSchema,
  /** the total fees has `add_to_subtotal` calcType */
  added: priceSchema,
  /** the total fees that has `subtract_from_subtotal` calcType */
  subtracted: priceSchema,
  /** the total fees that has `realized` calcType */
  realized: priceSchema,
  /** all fees applied which is realized + subtracted + added */
  all: priceSchema,
  /** the fees applied on the amount */
  applied: z.array(feeSchema),
});
export type AmountFees = z.infer<typeof amountFeesSchema>;

//* discount
export const discountSchema = z.object({
  /** discount value such 150 */
  amount: priceSchema,
  /** discount percentage such as 0.1 which means 10% */
  percentage: percentageSchema,
});
export type Discount = z.infer<typeof discountSchema>;

//* amount
export const amountSchema = z.object({
  /** the currency of all prices/amounts */
  currency: currencySchema,

  /** the total is what the user will pay for which is the sum of the following:
   * - (+) Tax
   * - (+) fees
   * - (+) items original subtotal
   * - (-) discount amount
   */
  total: priceSchema,
  /** total tax applied on subtotal */
  tax: multiTaxSchema,
  /** subtotal is the sum of the following:
   * - (+) items original subtotal
   * - (-) the discount amount
   */
  subtotal: priceSchema,
  /** items subtotal is amount `AFTER` discount but `BEFORE` any fees
   *
   * it's used for the main items cost before tax & fees.
   *
   * For example, this would be the shipping cost after discount (if any) but without any late fee and before any tax.
   */
  itemsSubtotal: priceSchema,

  /** realizedSubtotal = `itemsSubtotal` - `fees.realized` - `fees.subtracted` + `fees.added`
   *
   * realized subtotal is used when a fee cost that is realized within the itemsSubtotal.
   *
   * In other words, this amount gives you the amount you will receive before tax, which you can control.
   *
   * This is what you need to measure actually what customer are paying / carrier receiving / commission you're getting
   * without any direct costs hidden such as insurance_fees and taxes.
   */
  realizedSubtotal: priceSchema,
  /** the items amount `BEFORE` discount & tax & fees.
   *
   * Use this to show before & after discount amount
   * */
  itemsOriginalSubtotal: priceSchema,

  /** any fees that will be applied on amount such as insurance fees, late fees, etc */
  fees: amountFeesSchema,
  /** the discount amount & percentage applied */
  discount: discountSchema,
  /** the total paid amount */
  paid: priceSchema,
  /** the remaining amount to be paid */
  remaining: priceSchema,
});
export type Amount = z.infer<typeof amountSchema>;
